import type { Channel } from '@nexo/types';
import { Avatar, Spinner } from '@nexo/ui';
import { Hash, Lock } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useMessages } from '../../lib/queries';
import { channelLabel } from '../channels/ChannelSidebar';
import { MessageItem } from './MessageItem';
import { buildTimeline } from './timeline';
import { useTranslation } from 'react-i18next';

/**
 * Renders newest-at-bottom using `flex-col-reverse`, which keeps the view pinned to the latest
 * message and preserves the scroll position when older pages are prepended.
 */
export function MessageList({ channel }: { channel: Channel }) {
  const query = useMessages(channel.id);
  const sentinel = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  // Sending a message jumps back to the latest ones (scrollTop 0 is the bottom: column-reverse).
  useEffect(() => {
    const onScroll = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== channel.id) return;
      scroller.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('nexo:scroll-bottom', onScroll);
    return () => window.removeEventListener('nexo:scroll-bottom', onScroll);
  }, [channel.id]);

  // Pages are newest-first; each page is chronological.
  const messages = useMemo(
    () => (query.data ? [...query.data.pages].reverse().flatMap((p) => p.items) : []),
    [query.data],
  );
  const timeline = useMemo(() => buildTimeline(messages), [messages]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted">
        <Spinner className="size-5" />
      </div>
    );
  }

  return (
    <div
      ref={scroller}
      className="scrollbar-thin flex min-h-0 flex-1 flex-col-reverse overflow-y-auto"
    >
      <div className="flex flex-col pb-2">
        <div ref={sentinel} />
        {isFetchingNextPage && (
          <div className="flex justify-center py-3 text-muted">
            <Spinner />
          </div>
        )}
        {!hasNextPage && <ChannelIntro channel={channel} empty={messages.length === 0} />}
        {timeline.map((item) =>
          item.type === 'day' ? (
            <div
              key={item.key}
              role="separator"
              className="sticky top-2 z-10 my-3 flex justify-center"
            >
              <span className="rounded-full border border-border bg-surface/90 px-3 py-0.5 text-xs font-medium text-muted shadow-panel backdrop-blur">
                {item.label}
              </span>
            </div>
          ) : (
            <MessageItem key={item.key} message={item.message} compact={item.compact} />
          ),
        )}
      </div>
    </div>
  );
}

function ChannelIntro({ channel, empty }: { channel: Channel; empty: boolean }) {
  const { t } = useTranslation();
  const name = channelLabel(channel);
  const peer = channel.kind === 'dm' ? channel.dmPeer : null;
  return (
    <div className="px-6 pt-10 pb-4">
      {peer ? (
        <Avatar id={peer.id} name={peer.displayName} src={peer.avatarUrl} size="lg" />
      ) : (
        <span className="flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
          {channel.isPrivate ? <Lock className="size-6" /> : <Hash className="size-7" />}
        </span>
      )}
      <h2 className="mt-4 text-2xl font-semibold tracking-tight">
        {peer ? name : t('Te damos la bienvenida a #{{name}}', { name })}
      </h2>
      <p className="mt-1 max-w-lg text-sm text-muted">
        {peer
          ? t('Este es el principio de tu conversación privada con {{name}}.', { name })
          : channel.description
            ? t('{{description}}. Este es el principio del canal.', {
                description: channel.description,
              })
            : t('Este es el principio del canal.')}
        {empty && t(' Pasa el ratón por un mensaje para convertirlo en tarea.')}
      </p>
    </div>
  );
}
